/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useCallback, useState } from 'react';
import { Stepper } from './Stepper';
import Upload from './Steps/Upload';
import { useRsi } from '../hooks/useRsi';
import { StepState, StepType } from '../types';
import XLSX from 'xlsx-ugnis';
import { SelectHeaderStep } from './Steps/SelectHeader';
import { MatchColumnsStep } from './Steps/MatchColumns';
import { ValidationStep } from './Steps/Validation';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';


export const exceedsMaxRecords = (workSheet: XLSX.WorkSheet, maxRecords: number) => {
  const [top, bottom] = workSheet['!ref']?.split(':').map(position => parseInt(position.replace(/\D/g, ''), 10)) || [];
  return bottom - top > maxRecords;
};

// Task #66220: o `raw: false` do sheet_to_json devolve o texto formatado pelo xlsx, que usa sempre a
// convenção en-US (`,` milhares / `.` decimal) independentemente do formato regional do ficheiro. Um
// valor como `350 120` (Excel pt-PT) chega assim como "350,120", e o parse por locale a jusante (ver
// Utils.parseNumberAndUniformize) lê essa vírgula como decimal → 350,12. Nessas células — e só nessas —
// reescrevemos o texto a partir do número da folha, com espaço nos milhares: continua legível e deixa
// de ser ambíguo (o parse remove espaços). Células sem milhares agrupados (preços tipo "1.549",
// percentagens, datas, texto) ficam intactas.
const GROUPED_NUMBER = /\d,\d{3}(\D|$)/;

const withThousandsSpaces = (value: number): string => {
  const [integer, decimals] = String(value).split('.');
  const sign = integer.startsWith('-') ? '-' : '';
  const digits = sign ? integer.slice(1) : integer;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${sign}${grouped}${decimals ? `.${decimals}` : ''}`;
};

const unformatGroupedNumbers = (worksheet: XLSX.WorkSheet) => {
  const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null;
  if (!range) return;

  // Em modo `dense` (ver DropZone) as células vivem em worksheet['!data'][linha][coluna].
  const dense = (worksheet as any)['!data'] as any[][] | undefined;

  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = dense ? dense[r]?.[c] : (worksheet as any)[XLSX.utils.encode_cell({ r, c })];
      if (cell?.t === 'n' && typeof cell.v === 'number' && typeof cell.w === 'string' && GROUPED_NUMBER.test(cell.w)) {
        cell.w = withThousandsSpaces(cell.v);
      }
    }
  }
};

export const mapWorkbook = (workbook: XLSX.WorkBook, sheetName?: string) => {
  const worksheet = workbook.Sheets[sheetName || workbook.SheetNames[0]];
  unformatGroupedNumbers(worksheet);
  const data = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    blankrows: false,
    raw: false,
  });
  return data as string[][];
};

const HorizontalStepper = () => {
  const { initialStepState } = useRsi();
  const [state, setState] = useState<StepState>(initialStepState || { step: StepType.upload, selectHeader: {}, matchColumns: {}, validate: {} });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [steps, setSteps] = useState<string[]>([]);
  const { maxRecords } = useRsi();
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const errorToast = useCallback((description: string) => { addToast(description, { appearance: 'error' }) }, []);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({});

  const setNextStep = () => {
    setCompleted({ ...completed, [activeStep]: true });
    setActiveStep(activeStep + 1);
  };

  const setPreviousStep = () => {
    setActiveStep(activeStep - 1);
  };


  const getSteps = () => {
    setSteps([
      t('spreadsheet_import.upload_step.title'),
      t('spreadsheet_import.select_header_step.title'),
      t('spreadsheet_import.match_columns_step.title'),
      t('spreadsheet_import.validation_step.title')]);
  }

  useEffect(() => {
    getSteps();
  }, []);

  const stepsContent = [

    <Upload
      key={1}
      onContinue={async (workbook, file) => {
        setUploadedFile(file);
        const isSingleSheet = true; //workbook.SheetNames.length === 1;
        if (isSingleSheet) {
          if (maxRecords && exceedsMaxRecords(workbook.Sheets[workbook.SheetNames[0]], maxRecords)) {
            errorToast(t('spreadsheet_import.upload_step.max_records_exceeded', { maxRecords: maxRecords }));
            return;
          }
          try {
            const mappedWorkbook = mapWorkbook(workbook);
            
            setState({
              ...state,
              step: StepType.selectHeader,
              selectHeader: { data: mappedWorkbook, selectedRow: new Set([0]) },
              matchColumns: { data: [], matchedColumns: [] },
            });
            setNextStep();
          } catch (e) {
            errorToast((e as Error).message);
          }
        }
      }}
    />,

    <SelectHeaderStep
      key={2}
      selectedRow={state.selectHeader.selectedRow}
      //@ts-ignore
      data={state.selectHeader.data}
      onContinue={async (headerValues, data, selectedRow) => {
        let newMatchColumns = null;
        if (selectedRow == state.selectHeader.selectedRow) {
          newMatchColumns = state.matchColumns
        }

        try {
          setState({
            ...state,
            step: StepType.matchColumns,
            selectHeader: { ...state.selectHeader, selectedRow },
            matchColumns: { ...newMatchColumns, data, headerValues },
          });

          setNextStep();
          
        } catch (e) {
          errorToast((e as Error).message);
        }
      }}
      onPrevious={async () => {
        try {
          setState({ ...state, step: StepType.upload });
          setPreviousStep();
        } catch (e) {
          errorToast((e as Error).message);
        }
      }}
    />,
    
    <MatchColumnsStep
      key={3}
      data={state.matchColumns.data ?? []}
      matchedColumns={state.matchColumns.matchedColumns}
      headerValues={state.matchColumns.headerValues ?? []}
      onContinue={async (values, rawData, columns) => {

        try {

          const matchedOptions: { column: string, entry: string; value: string; }[] = [];

          columns.forEach(column => {
            if ('matchedOptions' in column && column.matchedOptions && column.matchedOptions.length > 0) {
              column.matchedOptions.forEach(mo => {
                if (mo.value && mo.entry) {
                  matchedOptions.push({ column: column.value, entry: mo.entry, value: mo.value });
                }
              });
            }
          });

          setState({
            ...state,
            step: StepType.validateData,
            matchColumns: {
              ...state.matchColumns,
              matchedColumns: columns
            },
            validate: {
              data: values,
              matchedOptions,
              matchedColumns: columns
            }
          });
          setNextStep();
        } catch (e) {
          errorToast((e as Error).message);
        }
      }}
      onPrevious={async (values, rawData, columns) => {
        try {
          const matchedOptions: { column: string, entry: string; value: string; }[] = [];
          columns.forEach(column => {
            if ('matchedOptions' in column && column.matchedOptions && column.matchedOptions.length > 0) {
              column.matchedOptions.forEach(mo => {
                if (mo.value && mo.entry) {
                  matchedOptions.push({ column: column.value, entry: mo.entry, value: mo.value });
                }
              });
            }
          });
          setState({
            ...state,
            step: StepType.selectHeader,
            matchColumns: {
              ...state.matchColumns,
              matchedColumns: columns
            },
            validate: {
              data: values,
              matchedOptions,
              matchedColumns: columns
            }
          });
          setPreviousStep();
        } catch (e) {
          errorToast((e as Error).message);
        }
      }}
    />,
    
    <ValidationStep
      key={4}
      initialData={state.validate.data ?? []}
      matchedOptions={state.validate.matchedOptions ?? []}
      matchedColumns={state.validate.matchedColumns ?? []}
      file={uploadedFile!}
      onPrevious={async () => {
        try {
          setState({ ...state, step: StepType.matchColumns });
          setPreviousStep();
        } catch (e) {
          errorToast((e as Error).message);
        }
      }}
    />
  ];

  return (
    <>
      <div style={{ flex: '0 0 auto', fontSize: '1rem' }}>
        <Stepper activeStep={activeStep} stepLabels={steps} ></Stepper>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', padding: '20px 24px', maxHeight: 'calc(95vh - 120px)', overflow: 'hidden' }}>
        {stepsContent[activeStep]}
      </div>
    </>
  );
};

export default HorizontalStepper;
